import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

const Services = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Updated services array to have exactly 6 items (3 per row)
  const services = [
    // First Row
    {
      icon: "🌍",
      title: "Adventure Tours",
      description: "Expertly crafted tours that blend excitement with cultural immersion.",
      gradient: "linear-gradient(135deg, #FF6B6B, #FF8E53)"
    },
    {
      icon: "🏕️",
      title: "Camping Experience",
      description: "Unique camping experiences in the heart of nature's wonders.",
      gradient: "linear-gradient(135deg, #4facfe, #00f2fe)"
    },
    {
      icon: "🚶‍♂️",
      title: "Guided Trails",
      description: "Professional guides leading you through scenic wilderness paths.",
      gradient: "linear-gradient(135deg, #43e97b, #38f9d7)"
    },
    // Second Row
    {
      icon: "🎯",
      title: "Custom Tours",
      description: "Personalized itineraries tailored to your preferences.",
      gradient: "linear-gradient(135deg, #fa709a, #fee140)"
    },
    {
      icon: "🌅",
      title: "Photography Tours",
      description: "Capture stunning moments in picturesque locations.",
      gradient: "linear-gradient(135deg, #7F7FD5, #86A8E7)"
    },
    {
      icon: "🏺",
      title: "Cultural Experiences",
      description: "Immersive experiences in local traditions and customs.",
      gradient: "linear-gradient(135deg, #5ee7df, #b490ca)"
    }
  ];

  // Split services into rows of three
  const firstRow = services.slice(0, 3);
  const secondRow = services.slice(3, 6);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <StyledServices ref={ref}>
      <Container>
        <motion.div
          initial="hidden"
          animate={controls}
          variants={containerVariants}
          className="services-wrapper"
        >
          <div className="section-header">
            <motion.span 
              className="section-tag"
              initial={{ opacity: 0, x: -20 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, x: 0 }
              }}
            >
              Our Services
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={controls}
              variants={{
                visible: { opacity: 1, y: 0 }
              }}
            >
              Extraordinary Experiences Await
            </motion.h2>
          </div>

          {/* First Row */}
          <Row className="services-grid mb-4">
            {firstRow.map((service, index) => (
              <Col md={4} key={index}>
                <motion.div
                  className="service-card"
                  variants={cardVariants}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  style={{
                    '--card-gradient': service.gradient
                  }}
                >
                  <div className="card-content">
                    <div className="icon-wrapper">
                      <span className="service-icon">{service.icon}</span>
                      <div className="icon-background"></div>
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <motion.button 
                      className="learn-more"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Learn More
                      <span className="arrow">→</span>
                    </motion.button>
                  </div>
                  <div className="card-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>

          {/* Second Row */}
          <Row className="services-grid">
            {secondRow.map((service, index) => (
              <Col md={4} key={index + 3}>
                <motion.div
                  className="service-card"
                  variants={cardVariants}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  style={{
                    '--card-gradient': service.gradient
                  }}
                >
                  <div className="card-content">
                    <div className="icon-wrapper">
                      <span className="service-icon">{service.icon}</span>
                      <div className="icon-background"></div>
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <motion.button 
                      className="learn-more"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Learn More
                      <span className="arrow">→</span>
                    </motion.button>
                  </div>
                  <div className="card-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>
      </Container>
    </StyledServices>
  );
};

const StyledServices = styled.section`
  padding: 120px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 100% 0%, rgba(45, 123, 168, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }

  .section-header {
    text-align: center;
    margin-bottom: 80px;

    .section-tag {
      display: inline-block;
      padding: 8px 20px;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      color: white;
      border-radius: 50px;
      font-size: 0.9rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 20px;
    }

    h2 {
      font-size: 3.5rem;
      font-weight: 700;
      background: linear-gradient(45deg, #1e293b, #334155);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0;
    }
  }

  .services-grid {
    margin-bottom: 30px;
  }

  .service-card {
    position: relative;
    background: white;
    border-radius: 30px;
    padding: 40px 30px;
    height: 100%;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: var(--card-gradient);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

      &::before {
        opacity: 0.05;
      }

      .icon-wrapper .icon-background {
        transform: scale(1.2);
      }

      .learn-more .arrow {
        transform: translateX(5px);
      }
    }

    .card-content {
      position: relative;
      z-index: 1;
    }

    .icon-wrapper {
      position: relative;
      width: 80px;
      height: 80px;
      margin-bottom: 25px;

      .service-icon {
        position: relative;
        z-index: 2;
        font-size: 2.5rem;
      }

      .icon-background {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        background: var(--card-gradient);
        opacity: 0.1;
        border-radius: 20px;
        transition: transform 0.3s ease;
      }
    }

    h3 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #1e293b;
      margin-bottom: 15px;
    }

    p {
      font-size: 1rem;
      line-height: 1.6;
      color: #64748b;
      margin-bottom: 25px;
    }

    .learn-more {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 0;
      background: none;
      border: none;
      color: #2d7ba8;
      font-weight: 600;
      cursor: pointer;

      .arrow {
        transition: transform 0.3s ease;
      }
    }

    .card-shapes {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      pointer-events: none;

      .shape {
        position: absolute;
        border-radius: 50%;
        
        &-1 {
          width: 100px;
          height: 100px;
          background: var(--card-gradient);
          opacity: 0.05;
          top: -50px;
          right: -50px;
        }
        
        &-2 {
          width: 70px;
          height: 70px;
          background: var(--card-gradient);
          opacity: 0.05;
          bottom: -35px;
          left: -35px;
        }
      }
    }
  }

  @media (max-width: 992px) {
    padding: 80px 0;

    .section-header {
      margin-bottom: 60px;

      h2 {
        font-size: 2.5rem;
      }
    }
  }

  @media (max-width: 768px) {
    .service-card {
      padding: 30px 20px;
      margin-bottom: 20px;
    }
  }
`;

export default Services;